import { SpecificSurveyPageSetup } from '@src/config/shell/remoteConfigTypes';

export type EnablingType = 'disable' | 'enableWithTimeout';

export type MultipleAnswersOptions = {
  enablingType: EnablingType;
  timeoutInMs?: number;
};

export interface ISurveyRenderManager {
  enableSurveyDisplay: () => boolean;
  setNextDisplayTime: (timeout?: number) => void;
  setHasAnswered: () => void;
}

export function useSurveyRenderManager(surveyId: string, userId: string, surveyPageSetup: SpecificSurveyPageSetup): ISurveyRenderManager {
    return useLocalStorageSurveyRenderManager(surveyId, userId, surveyPageSetup);
}

function useLocalStorageSurveyRenderManager(surveyId: string, userId: string, surveyPageSetup: SpecificSurveyPageSetup) {
    const answeredLocalStorageKey = `survey_answered_${surveyId}_${userId}`;
    const nextRenderTimeoutLocalStorageKey = `survey_nextDisplayTimeout_${userId}`;

    function wasSurveyAnswered(): boolean {
        return localStorage.getItem(answeredLocalStorageKey) !== null;
    }

    function didAnswerTimeOut(): boolean {
        const localStorageValue = localStorage.getItem(answeredLocalStorageKey);
        if (localStorageValue === 'true' && !surveyPageSetup.multipleAnswersTimeoutInMs) {
            return true;
        }
        return Date.now() > (parseInt(localStorageValue ?? '0') || 0);
    }

    function isDisplayFreezeTimedOut(): boolean {
        const nextRenderTime = parseInt(localStorage.getItem(nextRenderTimeoutLocalStorageKey) ?? '0') || 0;
        return Date.now() > nextRenderTime;
    }

    function setNextDisplayTime() {
        const timeout = surveyPageSetup.displayAgainTimeout || 0;
        localStorage.setItem(nextRenderTimeoutLocalStorageKey, (Date.now() + timeout).toString());
    }

    function setHasAnswered() {
        if (surveyPageSetup.multipleAnswersTimeoutInMs) {
            const nextDisplayTime = (Date.now() + surveyPageSetup.multipleAnswersTimeoutInMs).toString();
            localStorage.setItem(answeredLocalStorageKey, nextDisplayTime);
        } else {
            localStorage.setItem(answeredLocalStorageKey, 'true');
        }
    }

    function enableSurveyDisplay(): boolean {
        if (wasSurveyAnswered()) {
            return didAnswerTimeOut() && isDisplayFreezeTimedOut();
        }
        return isDisplayFreezeTimedOut();
    }

    return { enableSurveyDisplay, setNextDisplayTime, setHasAnswered };
}
